/**
 * "Send later": pick the moment a letter leaves.
 *
 * Three one-click times cover most of what people schedule for - in an hour,
 * tomorrow morning, Monday morning - and the calendar with its hour and minute
 * fields is under them for everything else. The calendar is shown straight
 * away rather than behind a trigger: this dialog exists to pick a time, and a
 * button inside it that opens a second layer to do that would be one click
 * too many (see `DateTimePanel`).
 *
 * The time is the browser's own local time; the server is given the instant.
 * Nothing is scheduled here - the choice goes on to the send confirmation, which
 * shows who the letter goes to beside when it goes, and schedules it from there.
 */
import { useMemo, useState } from "react";
import { CalendarClock } from "lucide-react";
import { DateTimePanel } from "@wa/ui";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import { Btn, Modal, ToggleChip } from "../../../ui";
import { formatSendTime, isSchedulable, quickPicks } from "./draftTime";

export function ScheduleModal({
  initial,
  onClose,
  onPick,
}: {
  /** The time already set, when a scheduled letter is being moved. */
  initial?: string | null;
  onClose: () => void;
  onPick: (iso: string) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  // Read once: the quick picks describe the moment the dialog opened, and a
  // list that re-rounded itself every render would move under the pointer.
  const [now] = useState(() => new Date());
  const picks = useMemo(() => quickPicks(now), [now]);
  const [value, setValue] = useState(initial ?? "");
  const ok = isSchedulable(value, new Date());

  return (
    <Modal title={t("drafts.scheduleTitle")} label={t("drafts.scheduleTitle")} onClose={onClose} className="max-w-[440px]">
      <div className="mt-1 flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {picks.map((p) => {
            const iso = p.at.toISOString();
            return (
              <ToggleChip key={p.key} active={value === iso} onClick={() => setValue(iso)}>
                <span className="font-[650]">{t(`drafts.quick.${p.key}`)}</span>
                <span className="crm-mut ml-1.5 tabular-nums">{formatSendTime(iso, locale)}</span>
              </ToggleChip>
            );
          })}
        </div>

        <DateTimePanel value={value} onChange={setValue} locale={locale} defaultHour={9} className="self-center" />

        {/* Said before the button is pressed, not after: a time in the past is
            the one mistake this dialog can catch on its own. */}
        {value && !ok ? <p className="text-[12.5px] text-destructive">{t("drafts.error.time_in_past")}</p> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose}>
            {t("common.cancel")}
          </Btn>
          <Btn type="button" tone="dark" disabled={!ok} onClick={() => onPick(value)}>
            <CalendarClock className="size-3.5" />
            {ok ? t("drafts.scheduleFor", { when: formatSendTime(value, locale) }) : t("drafts.schedule")}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
